import { Injectable } from '@angular/core';
import {
  HTTPWrapperContext,
  REQUEST_METHODS,
  RequestConfigImplementation,
  XHR_STRATEGIES,
  AxwayTokenFetcherService
} from '@ptl/xhr';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PRODUCT_GROUPS } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { CookieService } from 'ngx-cookie-service';
import { HttpHeaders } from '@angular/common/http';
import { EnrollmentAppStateStructure } from '../../../interfaces/enrollmentAppStateStructure.interface';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';

@Injectable({
  providedIn: 'root'
})
export class ExactTargetEmailService {
  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private cookieService: CookieService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  triggerSeniorMarketsConfirmationEmail(email: string) {
    try {
      const enrollmentState: EnrollmentAppStateStructure = this.reducerUtilityService.getState('EnrollmentApp');

      const selectedPlan = enrollmentState.submittedPlan;

      const isMedigap = (
        selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDIGAP.toUpperCase() ||
        selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDICARE_SUPPLEMENT.toUpperCase()
      );

      let brandCode = this.cookieService.get('brand');

      /** Medigap uses PBP_ID, MA and PDP use CMS_PLAN_ID, others use NULL */
      const productValue = (() => {
        if (brandCode === 'MINCR') {
          // TODO: We aren't selling MINCR/MN medigap? Why is this here?
          return isMedigap ? 'Medsup' : 'CostPlan';
        }

        if (isMedigap && selectedPlan.attributes.PBP_ID) {
          return selectedPlan.attributes.PBP_ID;
        } else if (!isMedigap) {
          if (selectedPlan.attributes.CMS_PLAN_ID && (
            selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDICARE_ADVANTAGE.toUpperCase() ||
            selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDIGAP.toUpperCase() ||
            selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDICARE_SUPPLEMENT.toUpperCase() ||
            selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.PRESCRIPTION_DRUG_PLAN.toUpperCase()

          )) {
            return selectedPlan.attributes.CMS_PLAN_ID;
          }
        } else {
          return null;
        }
      })();

      const planValue = (() => {
        if (brandCode === 'MINCR') {
          return true;
        }
        return isMedigap;
      })();

      // TODO: This should be sent to a brand aware service in case we use a different brand/ wy/mn/nd!
      const templateAndRegion = ((): { template: string; region: string; } => {
        switch (brandCode.toUpperCase()) {
          case 'HMBCBS':
            return {
              template: 'SnrAppConfirmHMBCBS',
              region: 'HMBCBS'
            };
          case 'NEPA':
            return {
              template: 'SnrAppConfirmNEPA',
              region: 'HMBCBS'
            };
          case 'HBS':
            return {
              template: 'SnrAppConfirmPBS',
              region: 'HBS'
            };
          case 'HMBCBSWV':
            return {
              template: 'SnrAppConfirmWV',
              region: 'HMBCBSWV'
            };
          case 'HMBCBSDE':
            return {
              template: 'SnrAppConfirmDE',
              region: 'HMBCBSDE'
            };
          case 'MINCR':
            return {
              template: 'SnrAppConfirmMN',
              region: 'MINCR'
            };
          case 'WYBCBS':
            return {
              template: 'SnrAppConfirmWY',
              region: 'WYBCBS'
            };
          case 'WNY':
            return {
              template: 'SnrAppConfirmWNY',
              region: 'WNY'
            };
          case 'NENY':
            return {
              template: 'SnrAppConfirmNENY',
              region: 'NENY'
            };
          case 'SEPA':
            return {
              template: 'SnrAppConfirmSEPA',
              region: 'SEPA'
            }
          default:
            console.error('CAN NOT FIND BRANDCODE FOR EMAIL')
        }
      })();

      const host = window.location.hostname;
      let env = '';
      if (host.indexOf('env') >= 0) 
          env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));

      const payload = {
        CONFNUMBER: enrollmentState.appId,
        TOEMAIL: email,
        PLAN: planValue,
        REGIONID: templateAndRegion.region,
        PRODUCT: productValue,
        TEMPLATENAME: templateAndRegion.template,
        RECEIVEDDATE: enrollmentState.submittedDate,
        BRAND: brandCode
      };
      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.EMAIL_SERVICES.APP_CONFIRMATION_EMAIL, {}),
        POST_OR_PUT_PAYLOAD: payload,
        headers: new HttpHeaders({
          'x-hmhs-test-backend': `wasinter${env}.highmark.com`
        })
      });

      this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
      return this.httpClient.makeRequest(requestConfig);
    } catch (e) {
      return Promise.reject(e);
    }
  }


  triggerIndividualMarketsConfirmationEmail(emailAddress: string, appId: string) {

    const payload = this.getIndividualMarketsConfirmationPayload(emailAddress, appId);

    const host = window.location.hostname;
    let env = '';
    if (host.indexOf('env') >= 0) 
        env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));
      
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.EMAIL_SERVICES.SEND_EMAIL_EXACTTARGET, {}),
      headers: new HttpHeaders({
        'x-hmhs-test-backend': `wasinter${env}.highmark.com`
      }),
      POST_OR_PUT_PAYLOAD: payload,
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  getIndividualMarketsConfirmationPayload(emailAddress: string, appId: string) {
    const exactTargetTemplates = {
      'HMBCBS': 'OffExchangeAppConfirmationWPA',
      'PBS': 'OffExchangeAppConfirmationCPA',
      'NEPA': 'OffExchangeAppConfirmationNEPA',
      'HMBCBSDE': 'OffExchangeAppConfirmationDE',
      'HMBCBSWV': 'OffExchangeAppConfirmationWV',
      'WNY': 'OffExchangeAppConfirmationWNY',
      'NENY': 'OffExchangeAppConfirmationNENY',
      'NDBCBS': 'HCREnrollmentsConfirmND',
      'WYBCBS': 'HCREnrollmentsConfirmWY',
      'MINCR': 'OffExchangeAppConfirmationMINCR',
      'SEPA': 'OffExchangeAppConfirmationSEPA',

      'HMBCBS_BEB': 'BEBAppConfirmationWPA',
      'PBS_BEB': 'BEBAppConfirmationCPA',
      'NEPA_BEB': 'BEBAppConfirmationNEPA',
      'HMBCBSDE_BEB': 'BEBAppConfirmationDE',
      'HMBCBSWV_BEB': 'BEBAppConfirmationWV',
      'WNY_BEB': 'BEBAppConfirmationWNY',
      'NENY_BEB': 'BEBAppConfirmationNENY',
      'SEPA_BEB': 'BEBAppConfirmationSEPA'
    };

    const exchangeType = 'OFFX'; // 'OFFX' || 'ONX'; // ONX never happens here
    const brandCode = this.cookieService.get('brand').toUpperCase();
    const productSuffix = this.getProductSuffix();
    const templateToUse = exactTargetTemplates[brandCode + productSuffix];

    // // region needs to be OFFX_6011options for ND and OFFX_6012 for WY
    let exchangeTypeCode = `${exchangeType}_${brandCode}`;
    if (brandCode === 'NDBCBS') {
      exchangeTypeCode = 'OFFX_6011';
    } else if (brandCode === 'WYBCBS') {
      exchangeTypeCode = 'OFFX_6012';
    }

    let payload = {
      baseMessage: {
        headerVersion: '1.0'
      },
      payload: {
        deliveryRecipient: emailAddress,
        deliveryMethod: 'EMAIL',
        templateName: templateToUse,
        etAttributes: [{
          'name': 'Region',
          'value': brandCode
        },
        {
          'name': 'exchangeType',
          'value': exchangeTypeCode
        }]
      }
    };

    if (this.isBlueEdgeBalance()) {
      payload.payload.etAttributes.push({
        'name': 'CONFNUMBER',
        'value': appId
      });
    }


    return payload;
  }

  getProductSuffix(): string {
    return this.isBlueEdgeBalance() ? '_BEB' : '';
  }

  isBlueEdgeBalance(): boolean {
    const enrollmentState: EnrollmentAppStateStructure = this.reducerUtilityService.getState('EnrollmentApp');
    const selectedPlan = enrollmentState.submittedPlan;

    return selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.BLUE_EDGE_BALANCE.toUpperCase();
  }
}
